import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header } from "../components/app/Header";
import { api } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Send, Loader2, Trash2, Sparkles } from "lucide-react";

const Situation = () => {
  const { key } = useParams();
  const navigate = useNavigate();
  const [situation, setSituation] = useState(null);
  const [tab, setTab] = useState("guide");

  useEffect(() => {
    api.get(`/situations/${key}`).then((r) => setSituation(r.data)).catch(() => navigate("/dashboard"));
  }, [key, navigate]);

  if (!situation) return null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bg)" }}>
      <Header />
      <div className="relative h-56 md:h-72 overflow-hidden" data-testid="situation-hero">
        <img src={situation.image} alt="" className="w-full h-full object-cover" />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(42,52,43,0.1) 0%, rgba(42,52,43,0.65) 100%)" }}
        />
        <div className="absolute inset-0 max-w-7xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-8 text-white">
          <button
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-1 text-sm mb-3 opacity-90 hover:opacity-100"
            data-testid="back-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4" /> All situations
          </button>
          <p className="text-xs uppercase tracking-[0.25em] opacity-80 mb-1">{situation.subtitle}</p>
          <h1 className="font-serif text-3xl md:text-5xl leading-tight">{situation.title}</h1>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <p className="max-w-2xl mb-10 text-lg" style={{ color: "var(--ink-soft)" }}>
          {situation.description}
        </p>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList
            className="bg-transparent p-0 h-auto gap-2 mb-8 flex-wrap"
            data-testid="situation-tabs"
          >
            {[
              ["guide", "Guided talk"],
              ["plan", "Action plan"],
              ["journal", "Journal"],
              ["resources", "Resources"],
            ].map(([v, l]) => (
              <TabsTrigger
                key={v}
                value={v}
                className="rounded-full px-5 py-2 data-[state=active]:text-white data-[state=active]:shadow-none"
                style={{
                  backgroundColor: tab === v ? "var(--terracotta)" : "var(--surface-2)",
                  color: tab === v ? "#fff" : "var(--ink)",
                }}
                data-testid={`tab-${v}`}
              >
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="guide">
            <ChatPanel situationKey={key} />
          </TabsContent>
          <TabsContent value="plan">
            <PlanPanel situationKey={key} />
          </TabsContent>
          <TabsContent value="journal">
            <JournalPanel situationKey={key} />
          </TabsContent>
          <TabsContent value="resources">
            <ResourcesPanel />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// ---------- Chat ----------
const ChatPanel = ({ situationKey }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get(`/chat/${situationKey}`).then((r) => setMessages(r.data));
  }, [situationKey]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    const optimistic = { id: `tmp-${Date.now()}`, role: "user", content: text };
    setMessages((m) => [...m, optimistic]);
    setInput("");
    try {
      const res = await api.post(`/chat/${situationKey}`, { message: text });
      setMessages((m) =>
        m.filter((x) => x.id !== optimistic.id).concat([res.data.user_message, res.data.assistant_message])
      );
    } catch {
      toast.error("Couldn't reach the guide");
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="rounded-3xl border bg-white flex flex-col"
      style={{ borderColor: "var(--border)", minHeight: "520px" }}
      data-testid="chat-panel"
    >
      <div className="flex-1 p-6 md:p-8 overflow-y-auto space-y-4" style={{ maxHeight: "520px" }}>
        {messages.length === 0 && (
          <div className="text-center py-12">
            <Sparkles className="w-6 h-6 mx-auto mb-3" style={{ color: "var(--terracotta)" }} />
            <p className="font-serif text-xl" style={{ color: "var(--ink)" }}>
              Say what's on your mind.
            </p>
            <p className="text-sm mt-2" style={{ color: "var(--ink-soft)" }}>
              Nothing is too small. The guide will meet you where you are.
            </p>
          </div>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`msg-${m.role}`}
          >
            <div
              className="max-w-[80%] rounded-2xl px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap"
              style={{
                backgroundColor: m.role === "user" ? "var(--terracotta)" : "var(--surface-2)",
                color: m.role === "user" ? "#fff" : "var(--ink)",
              }}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div
              className="rounded-2xl px-4 py-3 text-sm inline-flex items-center gap-2"
              style={{ backgroundColor: "var(--surface-2)", color: "var(--ink-soft)" }}
            >
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> thinking…
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <form
        onSubmit={send}
        className="border-t p-4 flex gap-2"
        style={{ borderColor: "var(--border)" }}
      >
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Share what's happening…"
          className="h-12 rounded-full bg-white"
          style={{ borderColor: "var(--border)" }}
          data-testid="chat-input"
          disabled={sending}
        />
        <Button
          type="submit"
          disabled={sending || !input.trim()}
          className="h-12 w-12 rounded-full p-0 text-white"
          style={{ backgroundColor: "var(--terracotta)" }}
          data-testid="chat-send-btn"
        >
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
};

// ---------- Plan ----------
const PlanPanel = ({ situationKey }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get(`/situations/${situationKey}/plan`).then((r) => setItems(r.data));
  }, [situationKey]);

  const toggle = async (item) => {
    const newDone = !item.done;
    setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, done: newDone } : x)));
    try {
      await api.patch(`/plan-items/${item.id}`, { done: newDone });
    } catch {
      toast.error("Couldn't save");
      setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, done: !newDone } : x)));
    }
  };

  const doneCount = items.filter((i) => i.done).length;

  return (
    <div
      className="rounded-3xl border bg-white p-6 md:p-10"
      style={{ borderColor: "var(--border)" }}
      data-testid="plan-panel"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>
            Your small plan
          </p>
          <h2 className="font-serif text-3xl" style={{ color: "var(--ink)" }}>
            One step at a time
          </h2>
        </div>
        <div className="text-right">
          <div className="font-serif text-4xl" style={{ color: "var(--terracotta)" }}>
            {doneCount}
            <span className="text-xl" style={{ color: "var(--ink-soft)" }}>
              /{items.length}
            </span>
          </div>
          <p className="text-xs uppercase tracking-[0.15em]" style={{ color: "var(--muted)" }}>
            Completed
          </p>
        </div>
      </div>

      <ul className="space-y-3">
        {items.map((item, idx) => (
          <li
            key={item.id}
            className="flex items-start gap-4 p-4 md:p-5 rounded-2xl border transition-colors"
            style={{
              borderColor: "var(--border)",
              backgroundColor: item.done ? "var(--surface-2)" : "var(--surface)",
            }}
            data-testid={`plan-item-${idx}`}
          >
            <Checkbox
              id={item.id}
              checked={item.done}
              onCheckedChange={() => toggle(item)}
              className="mt-1"
              data-testid={`plan-check-${idx}`}
            />
            <label
              htmlFor={item.id}
              className="flex-1 cursor-pointer text-[15px] leading-relaxed"
              style={{
                color: item.done ? "var(--ink-soft)" : "var(--ink)",
                textDecoration: item.done ? "line-through" : "none",
              }}
            >
              {item.text}
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ---------- Journal ----------
const JournalPanel = ({ situationKey }) => {
  const [entries, setEntries] = useState([]);
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/journal?situation_key=${situationKey}`).then((r) => setEntries(r.data));
  useEffect(() => {
    load();
  }, [situationKey]);

  const save = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSaving(true);
    try {
      await api.post("/journal", { situation_key: situationKey, title, content });
      setContent("");
      setTitle("");
      toast.success("Saved.");
      load();
    } catch {
      toast.error("Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/journal/${id}`);
      load();
    } catch {
      toast.error("Couldn't delete");
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-5" data-testid="journal-panel">
      <form
        onSubmit={save}
        className="lg:col-span-3 rounded-3xl border bg-white p-6 md:p-8 space-y-4"
        style={{ borderColor: "var(--border)" }}
      >
        <div>
          <p className="text-xs uppercase tracking-[0.2em] mb-1" style={{ color: "var(--muted)" }}>
            A new entry
          </p>
          <h2 className="font-serif text-3xl" style={{ color: "var(--ink)" }}>
            Write it down.
          </h2>
        </div>
        <Input
          placeholder="Title (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="h-11 rounded-xl bg-white"
          style={{ borderColor: "var(--border)" }}
          data-testid="journal-title-input"
        />
        <Textarea
          placeholder="What are you sitting with?"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="min-h-[220px] rounded-2xl bg-white"
          style={{ borderColor: "var(--border)" }}
          data-testid="journal-content-input"
        />
        <Button
          type="submit"
          disabled={saving || !content.trim()}
          className="rounded-full text-white"
          style={{ backgroundColor: "var(--terracotta)" }}
          data-testid="journal-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save entry"}
        </Button>
      </form>

      <div className="lg:col-span-2 space-y-3" data-testid="journal-list">
        {entries.length === 0 && (
          <div
            className="rounded-3xl border p-8 text-sm text-center"
            style={{ borderColor: "var(--border)", color: "var(--ink-soft)" }}
          >
            Your entries will live here.
          </div>
        )}
        {entries.map((e, i) => (
          <div
            key={e.id}
            className="rounded-2xl border p-5 bg-white relative group"
            style={{ borderColor: "var(--border)" }}
            data-testid={`journal-entry-${i}`}
          >
            <button
              onClick={() => remove(e.id)}
              className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ color: "var(--clay)" }}
              data-testid={`journal-delete-${i}`}
              aria-label="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            {e.title && (
              <p className="font-serif text-lg mb-1" style={{ color: "var(--ink)" }}>
                {e.title}
              </p>
            )}
            <p className="text-xs mb-2" style={{ color: "var(--muted)" }}>
              {new Date(e.created_at).toLocaleString()}
            </p>
            <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: "var(--ink-soft)" }}>
              {e.content}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Resources ----------
const ResourcesPanel = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/resources").then((r) => setItems(r.data));
  }, []);

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="resources-panel">
      {items.map((r, i) => (
        <a
          key={i}
          href={r.url}
          target="_blank"
          rel="noreferrer"
          className="rounded-2xl border p-6 bg-white transition-all hover:-translate-y-0.5 hover:shadow-sm"
          style={{ borderColor: "var(--border)" }}
          data-testid={`resource-${i}`}
        >
          <p className="text-xs uppercase tracking-[0.18em] mb-2" style={{ color: "var(--muted)" }}>
            {r.category}
          </p>
          <h3 className="font-serif text-xl mb-1" style={{ color: "var(--ink)" }}>
            {r.name}
          </h3>
          <p className="text-sm mb-2" style={{ color: "var(--ink-soft)" }}>
            {r.desc}
          </p>
          {r.phone && (
            <p className="text-sm" style={{ color: "var(--terracotta)" }}>
              Call {r.phone}
            </p>
          )}
        </a>
      ))}
    </div>
  );
};

export default Situation;
